import { useEffect, useMemo, useState } from 'react'
import { Download, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { inlineInput } from '@/lib/form-styles'
import { ladeTeilLv, leistungenAnfragen, lieferantenSuchen, type LeistungsAnfrageErgebnis } from '../../api/calculation-requests.api'
import { fetchRaster } from '../../api/calculation-positions.api'
import type { CalcPosition, Calculation } from '../../types/calculation.types'
import { AnfragenSenden, type ZuSenden } from './AnfragenSenden'

type Format = 'BOTH' | 'ONLV' | 'XLSX'

/**
 * AP-37 Detailblatt K 4.1 — **An Nachunternehmer**: gewählte LV-Positionen an gewählte
 * Partner (jede Position an jeden, Vergleichsangebote), je Position eine Belegzeile, das
 * Teil-LV (ONLV und/oder Excel) als Anlage der ersten Zeile. Nach dem Anlegen senden wie
 * die Materialanfragen (O 6.4). Ohne Belegwesen nur der Download der Datei (K-27).
 */
export function NuAnfrageDialog({
  kalkulation,
  lvFormat,
  vorgewaehlt,
  onClose,
  onDone,
}: {
  kalkulation: Calculation
  /** Format des Eingangs: GAEB → X83-Teil, sonst ONLV (ohne LV und bei B 2063 erzeugt, K-Erzeuger) */
  lvFormat?: string | null
  vorgewaehlt: string[]
  onClose: () => void
  onDone: () => void
}) {
  const lvName = lvFormat === 'GAEB' ? 'GAEB X83' : 'ONLV'
  const lvEndung = lvFormat === 'GAEB' ? 'x83' : 'onlv'
  const { hasModule } = useAuthStore()
  const belegwesen = hasModule('CORRESPONDENCE')
  const [positionen, setPositionen] = useState<CalcPosition[]>([])
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set(vorgewaehlt))
  const [suche, setSuche] = useState('')
  const [partner, setPartner] = useState<{ id: string; name: string; clientNumber: string }[]>([])
  const [empfaenger, setEmpfaenger] = useState<{ id: string; name: string }[]>([])
  const [format, setFormat] = useState<Format>(kalkulation.hasLv ? 'BOTH' : 'XLSX')
  const [langtext, setLangtext] = useState(true)
  const [frist, setFrist] = useState('')
  const [vorschau, setVorschau] = useState<LeistungsAnfrageErgebnis | null>(null)
  const [senden, setSenden] = useState<ZuSenden[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    fetchRaster(kalkulation.id)
      .then((r) => setPositionen(r.positions.filter((p) => p.kind !== 'TEXT')))
      .catch((e) => setFehler(apiErrorText(e, 'Die Positionen ließen sich nicht laden.')))
  }, [kalkulation.id])
  useEffect(() => {
    const t = setTimeout(() => void lieferantenSuchen(suche || undefined).then(setPartner).catch(() => setPartner([])), 250)
    return () => clearTimeout(t)
  }, [suche])

  const ids = useMemo(() => positionen.filter((p) => gewaehlt.has(p.id)).map((p) => p.id), [positionen, gewaehlt])
  const body = (dryRun: boolean) => ({
    calculationId: kalkulation.id,
    positionIds: ids,
    supplierIds: empfaenger.map((e) => e.id),
    attachmentFormat: format,
    withLongTexts: langtext,
    ...(frist ? { dueAt: frist } : {}),
    dryRun,
  })
  const tu = async (was: () => Promise<void>) => {
    setLaeuft(true)
    setFehler(null)
    try {
      await was()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  if (senden) {
    return (
      <AnfragenSenden
        belege={senden}
        onFertig={() => {
          setSenden(null)
          onDone()
        }}
      />
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">An Nachunternehmer</h2>
            <p className="text-xs text-muted-foreground">Je Partner eine Preisanfrage, je Position eine Belegzeile; das Teil-LV geht als Anlage mit. Antworten kommen über die Anfragen zurück.</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>Positionen ({ids.length})</span>
              <span className="flex gap-2">
                <button type="button" className="text-primary hover:underline" onClick={() => setGewaehlt(new Set(positionen.map((p) => p.id)))}>alle</button>
                <button type="button" className="text-primary hover:underline" onClick={() => setGewaehlt(new Set())}>keine</button>
              </span>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-md border border-border/60 p-1 text-sm">
              {positionen.map((p) => (
                <label key={p.id} className="flex items-start gap-2 px-1 py-0.5 hover:bg-accent/40">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={gewaehlt.has(p.id)}
                    onChange={(e) =>
                      setGewaehlt((s) => {
                        const n = new Set(s)
                        if (e.target.checked) n.add(p.id)
                        else n.delete(p.id)
                        return n
                      })
                    }
                  />
                  <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{p.displayNumber}</span>
                  <span className="min-w-0 flex-1 truncate">{p.shortText}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3 text-sm">
            {belegwesen ? (
              <div>
                <span className="block text-xs text-muted-foreground">Nachunternehmer (Partner mit Rolle Lieferant)</span>
                <div className="mb-1 flex flex-wrap gap-1">
                  {empfaenger.map((e) => (
                    <span key={e.id} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      {e.name}
                      <button type="button" aria-label={`${e.name} entfernen`} onClick={() => setEmpfaenger((l) => l.filter((x) => x.id !== e.id))}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input className={inlineInput} placeholder="Partner suchen …" value={suche} onChange={(e) => setSuche(e.target.value)} />
                <div className="mt-1 max-h-28 overflow-y-auto rounded-md border border-border/60">
                  {partner
                    .filter((p) => !empfaenger.some((e) => e.id === p.id))
                    .slice(0, 20)
                    .map((p) => (
                      <button key={p.id} type="button" className="block w-full px-2 py-0.5 text-left hover:bg-accent/40" onClick={() => setEmpfaenger((l) => [...l, { id: p.id, name: p.name }])}>
                        {p.name} <span className="text-xs text-muted-foreground">{p.clientNumber}</span>
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">Ohne Belegwesen gibt es keine Preisanfrage — das Teil-LV lässt sich herunterladen und selbst verschicken.</p>
            )}
            <div>
              <span className="block text-xs text-muted-foreground">Anlage</span>
              <select className={inlineInput} value={format} onChange={(e) => setFormat(e.target.value as Format)}>
                <option value="BOTH">{lvName} und Excel</option>
                <option value="ONLV">nur {lvName}</option>
                <option value="XLSX">nur Excel</option>
              </select>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={langtext} onChange={(e) => setLangtext(e.target.checked)} /> mit Langtexten
            </label>
            {belegwesen && (
              <label className="block">
                <span className="block text-xs text-muted-foreground">Antwort bis (leer: nach den Einstellungen)</span>
                <input type="date" className={inlineInput} value={frist} onChange={(e) => setFrist(e.target.value)} />
              </label>
            )}
            {!belegwesen && (
              <div className="flex flex-wrap gap-2">
                {(['ONLV', 'XLSX'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    disabled={!ids.length || laeuft}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 disabled:opacity-50"
                    onClick={() => void tu(() => ladeTeilLv(kalkulation.id, ids, f, `Teil-LV-${kalkulation.number}.${f === 'ONLV' ? lvEndung : 'xlsx'}`))}
                  >
                    <Download className="h-4 w-4" /> {f === 'ONLV' ? lvName : 'Excel'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {vorschau && (
          <div className="mt-4 rounded-md border border-border/60 p-3 text-sm">
            <div className="mb-1 font-medium">Vorschau</div>
            <ul className="space-y-0.5 text-xs">
              {vorschau.documents.map((d) => (
                <li key={d.supplierId}>
                  {d.supplierName}: {d.lines} Zeile(n), Anlage {d.attachments.join(', ')}, Antwort bis {new Date(d.dueAt).toLocaleDateString('de-DE')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {fehler && <p className="mt-3 text-sm text-destructive">{fehler}</p>}

        {belegwesen && (
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="button"
              disabled={!ids.length || !empfaenger.length || laeuft}
              className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
              onClick={() => void tu(async () => setVorschau(await leistungenAnfragen(body(true))))}
            >
              Vorschau
            </button>
            <button
              type="button"
              disabled={!ids.length || !empfaenger.length || laeuft}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              onClick={() =>
                void tu(async () => {
                  const r = await leistungenAnfragen(body(false))
                  if (r.failed.length) setFehler(r.failed.map((f) => `${f.supplierName}: ${f.message}`).join(' · '))
                  if (r.created.length) setSenden(r.created.map((c) => ({ documentId: c.documentId, supplierName: c.supplierName })))
                })
              }
            >
              Anlegen und senden
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
